import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { Company } from '../db/company.js';
import { JobApplication, JobApplicationStatus } from '../db/job-application.js';
import { JobPosition } from '../db/job-position.js';
import { CompanyBookingMail } from '../mails/booking-mails.js';
import { CandidateBookingMail } from '../mails/candidate-mails.js';
import { getJobApplicationByCurrentUserAndPosition } from './job-application.js';
import { getCompanyByID } from './company.js';

export const BOOK_INTERVIEW_SLOT_RESULT = { BOOKED: 'booked', BOOKED_MAIL_FAILED: 'booked-mail-failed' } as const;

/** Return a cancelled interview without duplicating or overlapping live capacity. */
export const releaseInterviewSlot = async (jobPositionID: string, start: Date, end: Date): Promise<boolean> => {
  if (end <= new Date()) return false;
  const result = await mongo()
    .collection<{
      _id: string;
      timeSlots: { _id: string; _class: string; start: Date; end: Date; isNonBlocking: boolean }[];
    }>(JobPosition._collectionName)
    .updateOne(
      { _id: jobPositionID, timeSlots: { $not: { $elemMatch: { start: { $lt: end }, end: { $gt: start } } } } },
      {
        $push: {
          timeSlots: { _id: A.getGloballyUniqueID(), _class: TimeSlot._class, start, end, isNonBlocking: false },
        },
      },
    );
  return result.modifiedCount === 1;
};

export const bookInterviewSlot = registerMutation({
  operationName: 'bookInterviewSlot',
  params: [
    { name: 'jobPositionID', type: () => ID },
    { name: 'timeSlotID', type: () => ID },
  ],
  type: () => String,
  resolve: (jobPositionID: string, timeSlotID: string) => async () => {
    const jobApplication = await getJobApplicationByCurrentUserAndPosition(
      jobPositionID,
      JobApplicationStatus.ACCEPTED,
    )({ _id: 1 });
    if (!jobApplication)
      throw new Error(
        `User (${context.serverContext ? undefined : context.userID}) not eligible for job position (${jobPositionID}) interview slot booking`,
      );

    const candidate = await getCandidate();
    const jobPositionCol = mongo().collection<EntityData<JobPosition>>(JobPosition._collectionName);
    const claimedJobPosition = await jobPositionCol.findOneAndUpdate(
      { _id: jobPositionID, timeSlots: { $elemMatch: { _id: timeSlotID } } },
      { $pull: { timeSlots: { _id: timeSlotID } } },
    );
    if (!claimedJobPosition) throw new Error('Dieser Termin ist nicht mehr verfügbar.');

    const claimedTimeSlot = claimedJobPosition.timeSlots?.find(slot => slot._id === timeSlotID);
    if (!claimedTimeSlot?.start || !claimedTimeSlot.end) {
      if (claimedTimeSlot)
        await jobPositionCol.updateOne({ _id: jobPositionID }, { $push: { timeSlots: claimedTimeSlot } });
      throw new Error('Dieser Termin ist nicht mehr verfügbar.');
    }

    const slotStart = new Date(claimedTimeSlot.start);
    const slotEnd = new Date(claimedTimeSlot.end);

    try {
      await updateJobApplication(jobApplication._id, slotStart, slotEnd);
    } catch (error) {
      let compensationError: unknown;
      try {
        await jobPositionCol.updateOne({ _id: jobPositionID }, { $push: { timeSlots: claimedTimeSlot } });
      } catch (caught) {
        compensationError = caught;
      }
      logger.error(
        { error, compensationError, jobPositionID, timeSlotID, slotStart, slotEnd },
        'Failed to persist interview booking after claiming time slot',
      );
      throw error;
    }

    let bookingResult: (typeof BOOK_INTERVIEW_SLOT_RESULT)[keyof typeof BOOK_INTERVIEW_SLOT_RESULT] =
      BOOK_INTERVIEW_SLOT_RESULT.BOOKED;
    try {
      await sendBookingMails(candidate, {
        companyID: claimedJobPosition.companyID,
        meetingLink: claimedJobPosition.meetingLink,
        title: claimedJobPosition.title,
        start: slotStart,
        end: slotEnd,
      });
    } catch (error) {
      logger.error({ error, jobPositionID, timeSlotID, slotStart, slotEnd }, 'Failed to send interview booking mails');
      bookingResult = BOOK_INTERVIEW_SLOT_RESULT.BOOKED_MAIL_FAILED;
    }

    return bookingResult;
  },
});

const getCandidate = async () => {
  //* Getting data
  const currUser = await getMyself<User>()({ _id: 1, email: 1, firstName: 1, lastName: 1 });
  const currentUserID = currUser?._id;
  if (!currentUserID) throw new NotLoggedInError();
  // currentUser returns the user calling this -> the candidate booking a slot
  const candidate = await getUserByID(currentUserID)({ _id: 1, email: 1, firstName: 1, lastName: 1, username: 1 });
  if (!candidate) throw new Error('Nicht angemeldet.');
  return candidate;
};

const sendBookingMails = async (
  candidate: NarrowedEntity<User, { _id: 1; email: 1; firstName: 1; lastName: 1; username: 1 }>,
  booking: {
    companyID: string | null | undefined;
    meetingLink: string | null | undefined;
    title: string | null | undefined;
    start: Date;
    end: Date;
  },
) => {
  const companyUsers = await getCompanyUsers(booking.companyID!);
  const companyUsersWithEmail = companyUsers.filter(u => u.email);

  const company = await mongo()
    .collection<Pick<EntityData<Company>, '_id' | '_class' | 'name'>>(Company._collectionName)
    .findOne({ _id: booking.companyID! }, { projection: { _class: 1, name: 1 } });
  if (!company) throw new Error('Did not find Company for companyID: ' + booking.companyID);

  const deDate = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'short' });
  const deTime = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });
  const tzLabel = 'Europe/Berlin';

  const formatTimeSlot = (start: Date, end: Date) =>
    `${deDate.format(start)} ${deTime.format(start)} - ${deTime.format(end)} (${tzLabel})`;

  //* Send emails
  const _mailer = await mailer;
  if (!_mailer) throw new Error('Mailer nicht konfiguriert.');
  logger.info('Sending appointment booking mails');

  if (candidate?.email) {
    await CandidateBookingMail(
      candidate,
      booking.meetingLink ?? '',
      booking.title ?? '',
      formatTimeSlot(booking.start, booking.end),
      company,
    ).send({ mailer: _mailer, recipients: new Recipient(candidate.email) });
  }

  for (const companyUser of companyUsersWithEmail) {
    await CompanyBookingMail(
      candidate,
      companyUser,
      booking.meetingLink ?? '',
      booking.title ?? '',
      formatTimeSlot(booking.start, booking.end),
    ).send({ mailer: _mailer, recipients: new Recipient(companyUser.email) });
  }
};

const updateJobApplication = async (jobApplicationID: string, interviewStart: Date, interviewEnd: Date) => {
  const jobApplicationCol = mongo().collection<JobApplication>(JobApplication._collectionName);
  const result = await jobApplicationCol.updateOne(
    { _id: jobApplicationID, status: JobApplicationStatus.ACCEPTED },
    { $set: { status: JobApplicationStatus.BOOKED, interviewStart, interviewEnd } },
  );
  if (result.matchedCount !== 1) throw new Error('Job application is no longer eligible for interview booking');
};

//TODO: imported in job-application.ts and cronjob.ts -> meh -> refactor architecture
export const getCompanyUsers = async (companyID: string) => {
  return runInServerContext(async () => {
    const company = await getCompanyByID(companyID)({ userIDs: 1 });
    const companyUserIDs = company?.userIDs ?? [];
    const companyUsers: NarrowedEntity<User, { _id: 1; email: 1; firstName: 1; lastName: 1; username: 1 }>[] = [];
    for (const uid of companyUserIDs) {
      const u = await getUserByID(uid)({ _id: 1, email: 1, firstName: 1, lastName: 1, username: 1 });
      if (u) companyUsers.push(u);
    }
    return companyUsers;
  });
};
